import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { DialogDefaultV2 } from '../../../components/v2';
import TableService from '../../../services/TableService';
import CalendarioCiclo from './CalendarioCiclo';
import ResumoTable from './ResumoTable';
import {
  URL_DATE_FORMAT,
  buildApiBody,
  filterPessoasPorRegime,
  formatPeriodLabel,
  pessoasFromFilterRows,
} from './constants';

const CalendarioGeralDialog = ({
  open,
  inicio,
  fim,
  dias,
  regime,
  loading,
  loadError,
  onClose,
  onOpenResumo,
  onOpenCalendario,
}) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayPessoas, setDayPessoas] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedDay(null);
      setDayPessoas(null);
      setDayError(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !selectedDay) {
      setDayPessoas(null);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setDayLoading(true);
      setDayError(false);
      try {
        const dayInicio = moment(selectedDay, URL_DATE_FORMAT).startOf('day').valueOf();
        const dayFim = moment(selectedDay, URL_DATE_FORMAT).endOf('day').valueOf();
        const all = [];
        let page = 0;
        let total = Infinity;
        while (all.length < total && page < 20) {
          const response = await TableService.request(
            `reports/conformidade-inspecao/filter?p=${page}&s=50`,
            buildApiBody(dayInicio, dayFim, { page, size: 50 }),
          );
          const rows = response?.data?.rows || [];
          total = Number(response?.data?.totalRows) || rows.length;
          all.push(...rows);
          if (!rows.length) break;
          page += 1;
        }
        if (!cancelled) {
          setDayPessoas(filterPessoasPorRegime(pessoasFromFilterRows(all), regime));
        }
      } catch {
        if (!cancelled) {
          setDayPessoas([]);
          setDayError(true);
        }
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, regime, selectedDay]);

  const diaLabel = selectedDay
    ? moment(selectedDay, URL_DATE_FORMAT).format('DD/MM/YYYY')
    : null;

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={`Calendário geral · ciclo ${formatPeriodLabel(inicio, fim)}`}
      readOnly
      maxWidth="lg"
      fullWidth
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível recortar o calendário deste regime.
        </Alert>
      )}
      <CalendarioCiclo
        inicio={inicio}
        fim={fim}
        dias={dias}
        selectedKey={selectedDay}
        onSelect={setSelectedDay}
      />
      {selectedDay && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {`Pessoas de ${diaLabel}`}
          </Typography>
          {dayLoading && <LinearProgress sx={{ mb: 1 }} />}
          {dayError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Não foi possível carregar o dia selecionado.
            </Alert>
          )}
          {!dayLoading && !dayError && (
            <ResumoTable
              pessoas={dayPessoas || []}
              diaSelecionado={selectedDay}
              onOpenResumo={onOpenResumo}
              onOpenCalendario={onOpenCalendario}
            />
          )}
        </Box>
      )}
    </DialogDefaultV2>
  );
};

CalendarioGeralDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  inicio: PropTypes.number,
  fim: PropTypes.number,
  dias: PropTypes.array,
  regime: PropTypes.string,
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onOpenResumo: PropTypes.func.isRequired,
  onOpenCalendario: PropTypes.func.isRequired,
};

export default CalendarioGeralDialog;
